import type {
  ApiContentConfigurationPayload,
  ApiContentConfiguration,
  UiContentConfiguration,
} from '../../schemas/contentConfiguration.schema';

import { DEFAULT_CURRENCY } from '../../config/constants';

export function mapContentConfigurationToApi(
  configuration: UiContentConfiguration,
): ApiContentConfigurationPayload {
  return {
    configuration: {
      // ABSENT, never coerced. The backend reads a present `marketplace` block
      // as an instruction — `enabled: false` soft-deletes the `Marketplaces`
      // row — and a present `isOrg` the same way. A caller that simply has no
      // opinion (a partial save such as the program editor's delete-exercise
      // PATCH) used to arrive here as `undefined` and leave as `false`, which
      // silently delisted the content and un-shared it from the organization.
      //
      // `meals.mappers.ts` and `nutritionPlans.mappers.ts` already build their
      // payloads this way; this brings the workout, program and exercise
      // families — the three that share this mapper — in line with them.
      ...(configuration.isOrg === undefined
        ? {}
        : { isOrg: configuration.isOrg }),
      ...(configuration.isMarketplace === undefined
        ? {}
        : {
            marketplace: {
              currency: configuration.marketplaceCurrency ?? DEFAULT_CURRENCY,
              enabled: configuration.isMarketplace,
              price: configuration.marketplacePrice ?? 0,
            },
          }),
      organizationIds: (configuration.organizationIds || []).map(
        (organizationId) => organizationId.value,
      ),
    },
  };
}
export function mapContentConfigurationFromApi(
  apiContentConfiguration?: ApiContentConfiguration,
): UiContentConfiguration {
  return {
    isMarketplace:
      apiContentConfiguration?.configuration?.marketplace?.enabled ?? false,
    isOrg: apiContentConfiguration?.configuration?.isOrg ?? false,
    isPremium: apiContentConfiguration?.isPremium ?? false,
    isPlatform: apiContentConfiguration?.isPlatform ?? false,
    marketplaceCurrency:
      apiContentConfiguration?.configuration?.marketplace?.currency ??
      DEFAULT_CURRENCY,
    marketplaceId:
      apiContentConfiguration?.configuration?.marketplace?.id ?? null,
    marketplaceInLibrary:
      apiContentConfiguration?.configuration?.marketplace?.inLibrary ?? false,
    // acquired, then removed from Saved — the CTA restores instead of buying
    marketplaceRestorable:
      apiContentConfiguration?.configuration?.marketplace?.restorable ?? false,
    marketplaceLibraryId:
      apiContentConfiguration?.configuration?.marketplace?.libraryId ?? null,
    // published content keeps its saved price (0 = explicitly free); content not
    // on the marketplace maps to null so the price input starts empty
    marketplacePrice: apiContentConfiguration?.configuration?.marketplace
      ?.enabled
      ? (apiContentConfiguration.configuration.marketplace.price ?? 0)
      : null,
    organizationIds:
      apiContentConfiguration?.configuration?.organizationIds?.map(
        (organizationId) => ({ label: '', value: organizationId }),
      ) ?? [],
  };
}
